<template>
    <div class="rank-list">
        <div v-for="(f, i) in top5" :key="f.code" :class="['rank-row', i === 0 && 'rank-row--top']">
            <div :class="['rank-badge', i === 0 && 'rank-badge--top']">{{ i + 1 }}</div>

            <div class="rank-body">
                <div class="rank-body-top">
                    <span class="rank-name-group">
                        <span :class="['rank-name', i === 0 && 'rank-name--top']">{{ f.name }}</span>
                        <span :class="['rank-scope-tag', `rank-scope-tag--s${f.scope}`]">S{{ f.scope }}</span>
                        <!-- <span v-if="f.facilityType || f.type" class="rank-type-text">{{
                            typeLabel(f.facilityType ?? f.type)
                        }}</span> -->
                    </span>
                    <span class="rank-stat">
                        <span class="rank-total num">{{ formatTotal(f.total) }}</span>
                        <span class="rank-ratio num">{{ (f.ratio ?? 0).toFixed(1) }}%</span>
                    </span>
                </div>

                <div class="rank-bar-row">
                    <div :class="['rank-bar-track', i === 0 && 'rank-bar-track--top']">
                        <div class="rank-bar-fill" :style="{ width: barPct(f) + '%', background: scopeColor(f) }"></div>
                    </div>
                    <span v-if="f.isNew" class="rank-yoy-new">신규</span>
                    <span
                        v-else-if="f.yoy != null"
                        :class="['rank-yoy num', f.yoy >= 0 ? 'rank-yoy--up' : 'rank-yoy--dn']"
                    >
                        {{ f.yoy >= 0 ? "▲" : "▼" }}{{ Math.abs(f.yoy).toFixed(1) }}%
                    </span>
                </div>
            </div>
        </div>

        <div v-if="others.length > 0" class="rank-others">
            <span
                ><strong class="rank-others-n">+{{ others.length }}개</strong> 사업장</span
            >
            <span class="num">{{ formatTotal(othersTotal) }} · {{ othersRatio.toFixed(1) }}%</span>
        </div>
    </div>
</template>

<script>
const C_SCOPE1 = "#b88330";
const C_SCOPE2 = "#1e7a63";

const FACILITY_TYPE_MAP = {
    FACTORY: "공장",
    OFFICE: "사무소",
    WAREHOUSE: "창고",
    STORE: "매장",
    PLANT: "플랜트",
};

export default {
    name: "FacilityRankTab",
    props: {
        sorted: { type: Array, default: () => [] },
        apiUnit: { type: String, default: "kgCO2e" },
        displayUnit: { type: String, default: "kgCO2e" },
    },
    computed: {
        top5() {
            return this.sorted.slice(0, 5);
        },
        others() {
            return this.sorted.slice(5);
        },
        othersTotal() {
            return this.others.reduce((s, f) => s + (f.total ?? 0), 0);
        },
        othersRatio() {
            return this.others.reduce((s, f) => s + (f.ratio ?? 0), 0);
        },
    },
    methods: {
        barPct(f) {
            const max = this.sorted[0]?.total || 1;
            return ((f.total ?? 0) / max) * 100;
        },
        scopeColor(f) {
            return f.scope === 2 ? C_SCOPE2 : C_SCOPE1;
        },
        formatTotal(v) {
            if (!v) return "—";
            let val = Number(v);
            if (this.apiUnit === "kgCO2e" && this.displayUnit === "tCO2e") val = val / 1000;
            else if (this.apiUnit === "tCO2e" && this.displayUnit === "kgCO2e") val = val * 1000;
            const unit = this.displayUnit === "tCO2e" ? "t" : "kg";
            return `${Math.round(val).toLocaleString("ko-KR")} ${unit}`;
        },
        typeLabel(t) {
            return FACILITY_TYPE_MAP[t] || t || "";
        },
    },
};
</script>

<style scoped>
.num {
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum" 1;
}

.rank-list {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--dash-hairline-soft) transparent;
}
.rank-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 6px 10px;
    border-radius: 10px;
    transition: background 0.12s;
    cursor: default;
}
.rank-row:hover {
    background: var(--dash-surface-2);
}
.rank-row--top {
    padding: 8px 12px;
    background: var(--dash-accent-wash);
}
.rank-row--top:hover {
    background: var(--dash-accent-tint);
}

.rank-badge {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--dash-surface-2);
    color: var(--dash-ink-soft);
    font-size: 11px;
    font-weight: 700;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    margin-top: 1px;
}
.rank-badge--top {
    background: var(--dash-accent);
    color: #fff;
}

.rank-body {
    flex: 1;
    min-width: 0;
}

.rank-body-top {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}
.rank-name-group {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    min-width: 0;
}
.rank-name {
    font-size: 13px;
    font-weight: 700;
    color: var(--dash-ink);
    white-space: nowrap;
    text-overflow: ellipsis;
    min-width: 0;
    flex-shrink: 1;
}
.rank-name--top {
    font-size: 14px;
}

.rank-stat {
    display: flex;
    align-items: baseline;
    gap: 5px;
    flex-shrink: 0;
}
.rank-total {
    font-size: 12px;
    font-weight: 600;
    color: var(--dash-ink);
}
.rank-ratio {
    font-size: 11px;
    color: var(--dash-ink-mute);
}

.rank-bar-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}
.rank-bar-track {
    flex: 1;
    height: 3px;
    background: var(--dash-hairline-soft);
    border-radius: 999px;
}
.rank-bar-track--top {
    height: 5px;
}
.rank-bar-fill {
    height: 100%;
    border-radius: 999px;
    transition: width 0.45s ease;
}

.rank-yoy {
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
}
.rank-yoy--up {
    color: #c07820;
}
.rank-yoy--dn {
    color: #1e7a63;
}
.rank-yoy-new {
    font-size: 9px;
    font-weight: 700;
    color: #c07820;
    background: rgba(192, 120, 32, 0.12);
    padding: 1px 6px;
    border-radius: 999px;
    flex-shrink: 0;
}

.rank-scope-tag {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.04em;
    padding: 1px 5px;
    border-radius: 7px;
}
.rank-scope-tag--s1 {
    color: #b88330;
    background: rgba(184, 131, 48, 0.12);
}
.rank-scope-tag--s2 {
    color: #1e7a63;
    background: rgba(30, 122, 99, 0.12);
}
.rank-type-text {
    font-size: 10px;
    color: var(--dash-ink-mute);
}

.rank-others {
    margin-top: 4px;
    padding: 7px 12px;
    border-radius: 8px;
    background: var(--dash-surface-2);
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    color: var(--dash-ink-soft);
    flex-shrink: 0;
}
.rank-others-n {
    font-weight: 700;
    color: var(--dash-ink);
}
</style>
